import Select from '@ben/orion/deprecated-select';
import Input from '@ben/orion/input';
import { SliderValueType } from '@ben/shared/types/columbia';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import useMinMaxInput from '../../../../hooks/use-min-max-input';
import { fieldCannotBeBlankErrorMessage } from '../../../shared/constants';
import MinMaxConfiguration from '../_common/min-max-configuration';

interface IModularSliderInputProps {
  componentControllerName: string;
}

const ModularSliderInput = ({
  componentControllerName,
}: IModularSliderInputProps) => {
  const form = useFormContext();
  const { control } = form;

  const { isFixedNumber, setIsFixedNumber, renderMinMaxInput } = useMinMaxInput(
    {
      componentControllerName,
      form,
      validationMessage: {
        min: 'Min value should be less than max value',
        max: 'Max value should be greater than min value',
      },
    }
  );

  return (
    <>
      <ComponentsWrapper>
        <Controller
          name={`${componentControllerName}.label`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Slider label"
              maxLength={30}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              width="100%"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
        />
        <Controller
          name={`${componentControllerName}.sliderValueType`}
          control={control}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              name="Slider value type"
              hintText="Defines the format of the value displayed."
              options={[
                {
                  key: SliderValueType.NO_SYMBOL,
                  label: 'No symbol (e.g. “1”)',
                },
                {
                  key: SliderValueType.PERCENTAGE,
                  label: 'Percentage (e.g. “2%”)',
                },
              ]}
              defaultValue={field.value}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              width="100%"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
        />
      </ComponentsWrapper>
      <ComponentsWrapper>
        <Controller
          name={`${componentControllerName}.default`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Default value (optional)"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
              min={0}
              step={0.01}
            />
          )}
          rules={{
            validate: (value) => {
              if (!value || !isFixedNumber) return true;
              const min = form.watch(`${componentControllerName}.min`);
              const max = form.watch(`${componentControllerName}.max`);
              if (
                min &&
                max &&
                (Number(value) < Number(min) || Number(value) > Number(max))
              ) {
                return 'Default value should be between min and max values';
              }
              return true;
            },
          }}
        />
        <Controller
          name={`${componentControllerName}.increment`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Increment"
              hintText="Set the amount by which the slider moves when adjusted."
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              defaultValue={1}
              type="number"
              min={0.01}
              step={0.01}
            />
          )}
        />
      </ComponentsWrapper>
      <MinMaxConfiguration
        isFixedNumber={isFixedNumber}
        setIsFixedNumber={setIsFixedNumber}
      />
      <ComponentsWrapper>
        {renderMinMaxInput({
          isMin: true,
          name: 'Min value accepted',
          inputProps: { min: 0, step: 0.01 },
        })}
        {renderMinMaxInput({
          isMin: false,
          name: 'Max value accepted',
          inputProps: { min: 0, step: 0.01 },
        })}
      </ComponentsWrapper>
    </>
  );
};

export default ModularSliderInput;

const ComponentsWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;
